// ============================================================================
// Lógica de préstamos (cuotas, cronograma, saldos) — FUENTE ÚNICA.
//
// Antes esta lógica estaba copiada en 4+ lugares (frontend y app móvil) y
// cada copia calculaba un poco distinto. Así aparecieron casi todos los bugs
// de préstamos del sistema (Ana, Paul, Kevin, Andrea, Javier, Wilman, Laura).
//
// REGLA: toda pantalla o endpoint que calcule cuotas, cronogramas o saldos
// debe usar las funciones de este archivo. El backend lo carga directamente
// (ver services/amortizacion.js) — no se copia.
//
// Pruebas automáticas: amortizacion.test.js (cubre cada bug real). Correr con
//   cd client && CI=true npx react-scripts test --watchAll=false
// ============================================================================

export const NOMBRES_MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export const getNombreMes = (mes) => NOMBRES_MESES[mes - 1] || '';

// Estados que cuentan como "préstamo activo" (dinero otorgado, sin finalizar).
export const ESTADOS_PRESTAMO_ACTIVO = ['aprobada', 'aprobado', 'desembolsado'];

export const esPrestamoActivo = (estado) => ESTADOS_PRESTAMO_ACTIVO.includes(estado);

export const encontrarPrestamoActivo = (prestamos = []) =>
  prestamos.find(p => esPrestamoActivo(p.estado)) || null;

// Margen para comparar montos de dinero sin que los centavos de redondeo
// dejen un préstamo "abierto" para siempre.
const CENTAVO = 0.01;

const primerDiaDelMes = (fecha) => new Date(fecha.getFullYear(), fecha.getMonth(), 1);
const fechaDeRegistro = (r) => new Date(r.año, r.mes - 1, 1);
const tienePagoDePrestamo = (r) => (r.capitalPrestamo > 0 || r.interesPrestamo > 0);
const estaEtiquetado = (r) => !!(r.solicitudPrestamo && r.solicitudPrestamo._id);
const etiquetadoA = (r, prestamo) => estaEtiquetado(r) && String(r.solicitudPrestamo._id) === String(prestamo._id);
const mesesEntre = (desde, hasta) =>
  (hasta.getFullYear() - desde.getFullYear()) * 12 + (hasta.getMonth() - desde.getMonth());

/**
 * Cuota mensual y desglose de un préstamo.
 *
 * Usa el `interesTotal` GUARDADO en la solicitud; nunca lo recalcula desde
 * `tasaInteres` (monto × tasa × plazo daba $6.98 en vez de los $7.00 de Ana).
 */
export const calcularCuotaMensual = (prestamo) => {
  if (!prestamo) {
    return { monto: 0, plazo: 0, tasaMensual: 0, cuotaMensual: 0, interesAlMes: 0, interesTotal: 0, totalAPagar: 0 };
  }

  const monto = prestamo.monto || 0;
  const plazo = prestamo.plazo || 0;
  const interesTotal = prestamo.interesTotal || 0;
  const tasaMensual = prestamo.tasaInteres || 0; // solo referencial

  const cuotaMensual = plazo > 0 ? (monto + interesTotal) / plazo : 0;
  const interesAlMes = plazo > 0 ? interesTotal / plazo : 0;

  return {
    monto,
    plazo,
    tasaMensual,
    cuotaMensual: parseFloat(cuotaMensual.toFixed(2)),
    interesAlMes: parseFloat(interesAlMes.toFixed(2)),
    interesTotal,
    totalAPagar: monto + interesTotal
  };
};

/**
 * Determina el mes de inicio del préstamo y qué registros mensuales le
 * pertenecen. TODO cálculo de saldo usa esto — es la única definición de
 * "este pago es de este préstamo".
 *
 *  - Ancla normal: desembolso → aprobación → solicitud.
 *  - Si hay pagos ETIQUETADOS a este préstamo antes del ancla (aprobación
 *    registrada tarde, el socio ya pagaba), el ancla se adelanta a ese pago
 *    (bug de Wilman).
 *  - Un registro etiquetado pertenece SOLO a su préstamo (bug de Kevin/Javier:
 *    el pago final de un préstamo viejo se colaba en el nuevo).
 *  - Un registro sin etiqueta se asigna por fecha (desde el ancla).
 */
export const obtenerRegistrosDelPrestamo = (prestamo, registrosDelSocio = []) => {
  let ancla = primerDiaDelMes(new Date(prestamo.fechaDesembolso || prestamo.fechaAprobacion || prestamo.fechaSolicitud));

  const pagosEtiquetados = registrosDelSocio.filter(r => etiquetadoA(r, prestamo) && tienePagoDePrestamo(r));
  for (const r of pagosEtiquetados) {
    const f = fechaDeRegistro(r);
    if (f < ancla) ancla = f;
  }

  const registros = registrosDelSocio.filter(r => {
    if (estaEtiquetado(r)) return etiquetadoA(r, prestamo);
    return fechaDeRegistro(r) >= ancla;
  });

  return { ancla, registros };
};

/**
 * Hasta qué mes corre el préstamo. Si está FINALIZADO, deja de generar
 * interés y períodos desde que se cerró (fecha de finalización, o el último
 * mes con pago si esa fecha no se guardó). Si está activo, corre hasta hoy.
 */
const calcularFechaCorte = (prestamo, registrosDelPrestamo, hoy) => {
  if (prestamo.estado !== 'finalizada') return primerDiaDelMes(hoy);

  const pagos = registrosDelPrestamo.filter(tienePagoDePrestamo).map(fechaDeRegistro);
  const ultimoPago = pagos.length > 0 ? new Date(Math.max(...pagos)) : null;

  if (prestamo.fechaFinalizacion) {
    const fin = primerDiaDelMes(new Date(prestamo.fechaFinalizacion));
    // Si se registró algún pago después de marcarlo finalizado, mostrarlo igual.
    return ultimoPago && ultimoPago > fin ? ultimoPago : fin;
  }
  return ultimoPago || primerDiaDelMes(hoy);
};

/**
 * Cronograma de amortización mes a mes.
 *
 * Reglas (cada una resolvió un bug real):
 *  - Período 1 = mes siguiente al ancla, salvo que haya pago en el mismo mes
 *    del ancla: entonces ese mes es el Período 1 (bug de Andrea/Paul).
 *  - El interés corre con el calendario real aunque el socio no pague
 *    (socio moroso; bug original de Ana).
 *  - Un préstamo FINALIZADO no genera más interés ni períodos después de
 *    su cierre, y su saldo final es $0 (bug de Laura/Kevin).
 *  - Sin tope fijo de meses: antes un tope de 24 cortaba préstamos largos
 *    antes de su propio plazo.
 */
export const generarCronogramaAmortizacion = (prestamo, detalles, registrosDelSocio = []) => {
  const cronograma = [];
  const plazo = detalles?.plazo || 0;
  const cuotaMensual = detalles?.cuotaMensual || 0;
  const interesAlMes = detalles?.interesAlMes || 0;
  const pagosPrestamo = prestamo.pagos || [];
  const finalizado = prestamo.estado === 'finalizada';

  const hoy = new Date();
  const mesActual = primerDiaDelMes(hoy);

  const { ancla, registros: registrosDelPrestamo } = obtenerRegistrosDelPrestamo(prestamo, registrosDelSocio);
  const fechaCorte = calcularFechaCorte(prestamo, registrosDelPrestamo, hoy);

  // Hasta qué período generar: el plazo, o más lejos si hay pagos o meses
  // transcurridos después del plazo (+3 meses de proyección si sigue abierto).
  let mesMaximo = Math.max(plazo, mesesEntre(ancla, fechaCorte));
  for (const pago of pagosPrestamo) {
    if (pago.fecha) mesMaximo = Math.max(mesMaximo, mesesEntre(ancla, new Date(pago.fecha)));
  }
  for (const r of registrosDelPrestamo) {
    mesMaximo = Math.max(mesMaximo, mesesEntre(ancla, fechaDeRegistro(r)));
  }
  const plazoFinal = finalizado ? mesMaximo : mesMaximo + 3;

  const pagoEnMesDeAncla = registrosDelPrestamo.some(r =>
    fechaDeRegistro(r).getTime() === ancla.getTime() && tienePagoDePrestamo(r)
  );
  const inicioPeriodo = pagoEnMesDeAncla ? 0 : 1;

  let saldoRestante = detalles?.monto || 0;
  let interesPagadoAcumulado = 0;

  for (let i = inicioPeriodo; i <= plazoFinal; i++) {
    const periodoNumero = i - inicioPeriodo + 1;
    const fechaVencimiento = new Date(ancla.getFullYear(), ancla.getMonth() + i, 1);

    // Un préstamo finalizado no sigue más allá de su cierre
    if (finalizado && fechaVencimiento > fechaCorte) break;

    let pagoPeriodo = pagosPrestamo.find(p => {
      if (!p.fecha) return false;
      const f = new Date(p.fecha);
      return f.getMonth() === fechaVencimiento.getMonth() && f.getFullYear() === fechaVencimiento.getFullYear();
    });

    let registroMes = null;
    if (!pagoPeriodo) {
      registroMes = registrosDelPrestamo.find(r =>
        fechaDeRegistro(r).getTime() === fechaVencimiento.getTime() && tienePagoDePrestamo(r)
      );
      if (registroMes) {
        pagoPeriodo = {
          fecha: fechaVencimiento,
          monto: (registroMes.capitalPrestamo || 0) + (registroMes.interesPrestamo || 0),
          interes: registroMes.interesPrestamo || 0,
          capital: registroMes.capitalPrestamo || 0
        };
      }
    }

    let capitalPagado = 0;
    let interesPagado = 0;
    if (registroMes) {
      capitalPagado = registroMes.capitalPrestamo || 0;
      interesPagado = registroMes.interesPrestamo || 0;
    } else if (pagoPeriodo) {
      capitalPagado = cuotaMensual - interesAlMes;
      interesPagado = interesAlMes;
    }

    // Solo el pago de capital reduce el saldo
    const nuevoSaldo = capitalPagado > 0 ? Math.max(0, saldoRestante - capitalPagado) : saldoRestante;
    interesPagadoAcumulado += interesPagado;

    let estado = 'No vencido';
    if (pagoPeriodo) {
      estado = capitalPagado > 0 ? 'Pagado' : (interesPagado > 0 ? 'Pagó Interés' : 'Pagado');
    } else if (fechaVencimiento < mesActual) {
      estado = 'Impago';
    } else if (fechaVencimiento.getTime() === mesActual.getTime()) {
      estado = 'Pendiente';
    }

    const interesGenerado = interesAlMes * periodoNumero;
    const interesPendiente = Math.max(0, interesGenerado - interesPagadoAcumulado);

    cronograma.push({
      periodo: periodoNumero,
      mes: getNombreMes(fechaVencimiento.getMonth() + 1),
      año: fechaVencimiento.getFullYear(),
      fechaMes: `${fechaVencimiento.getFullYear()}-${String(fechaVencimiento.getMonth() + 1).padStart(2, '0')}`,
      fechaVencimiento: fechaVencimiento.toLocaleDateString('es-ES'),
      cuotaMensual: cuotaMensual.toFixed(2),
      interes: interesAlMes.toFixed(2),
      estado,
      pagado: !!pagoPeriodo,
      capitalPagado: capitalPagado.toFixed(2),
      interesPagado: interesPagado.toFixed(2),
      saldoRestante: nuevoSaldo.toFixed(2),
      saldoTotalConInteres: (nuevoSaldo + interesPendiente).toFixed(2)
    });

    saldoRestante = nuevoSaldo;
    if (nuevoSaldo <= CENTAVO) break;
  }

  // Finalizado = cerrado: no queda nada por cobrar, ni capital ni interés.
  if (finalizado && cronograma.length > 0) {
    const ultimo = cronograma[cronograma.length - 1];
    ultimo.saldoRestante = '0.00';
    ultimo.saldoTotalConInteres = '0.00';
    ultimo.finalizado = true;
  }

  return cronograma;
};

const fechaDeFila = (fila) => new Date(fila.año, NOMBRES_MESES.indexOf(fila.mes), 1);

/**
 * La fila del cronograma que corresponde a HOY.
 *
 *  - Si hay fila para el mes actual, esa.
 *  - Si hoy es antes del Período 1 (préstamo recién aprobado), el monto
 *    original sin interés todavía.
 *  - Si hoy es después de la última fila (ya pagado o cerrado), la última.
 *  - Nunca una fila FUTURA de proyección (bug original de Ana).
 */
export const obtenerPeriodoDeHoy = (cronograma) => {
  if (!cronograma || cronograma.length === 0) return null;
  const mesActual = primerDiaDelMes(new Date());

  const periodoDeHoy = cronograma.find(p => fechaDeFila(p).getTime() === mesActual.getTime());
  if (periodoDeHoy) return periodoDeHoy;

  const primero = cronograma[0];
  if (mesActual < fechaDeFila(primero)) {
    const montoOriginal = parseFloat(primero.saldoRestante) + parseFloat(primero.capitalPagado);
    return {
      ...primero,
      periodo: 0,
      mes: getNombreMes(mesActual.getMonth() + 1),
      año: mesActual.getFullYear(),
      estado: 'No vencido',
      pagado: false,
      capitalPagado: '0.00',
      interesPagado: '0.00',
      saldoRestante: montoOriginal.toFixed(2),
      saldoTotalConInteres: montoOriginal.toFixed(2)
    };
  }

  // Última fila que no sea futura
  const pasadas = cronograma.filter(p => fechaDeFila(p) <= mesActual);
  return pasadas.length > 0 ? pasadas[pasadas.length - 1] : cronograma[cronograma.length - 1];
};

/**
 * Saldo pendiente de UN préstamo hoy. Un préstamo finalizado debe $0.
 */
export const calcularDeudaPendiente = (prestamo, registrosDelSocio = []) => {
  if (!prestamo || prestamo.estado === 'finalizada') return { sinInteres: 0, conInteres: 0 };

  const detalles = calcularCuotaMensual(prestamo);
  const cronograma = generarCronogramaAmortizacion(prestamo, detalles, registrosDelSocio);
  const periodoDeHoy = obtenerPeriodoDeHoy(cronograma);
  if (!periodoDeHoy) return { sinInteres: detalles.monto, conInteres: detalles.monto };

  return {
    sinInteres: Math.round(parseFloat(periodoDeHoy.saldoRestante) * 100) / 100,
    conInteres: Math.round(parseFloat(periodoDeHoy.saldoTotalConInteres) * 100) / 100
  };
};

/**
 * Resumen de pagos y saldo de un préstamo, para listados y para decidir si
 * se puede finalizar. Todo sale del mismo cronograma — los números coinciden
 * con lo que se ve en la tabla.
 */
export const calcularSaldoPrestamo = (prestamo, registrosDelSocio = []) => {
  const detalles = calcularCuotaMensual(prestamo);
  const { registros } = obtenerRegistrosDelPrestamo(prestamo, registrosDelSocio);
  const conPago = registros.filter(tienePagoDePrestamo);

  const capitalPagado = conPago.reduce((s, r) => s + (r.capitalPrestamo || 0), 0);
  const interesPagado = conPago.reduce((s, r) => s + (r.interesPrestamo || 0), 0);
  const deuda = calcularDeudaPendiente(prestamo, registrosDelSocio);

  return {
    capitalPagado,
    interesPagado,
    totalPagado: capitalPagado + interesPagado,
    capitalPendiente: deuda.sinInteres,
    interesPendiente: Math.max(0, deuda.conInteres - deuda.sinInteres),
    totalPendiente: deuda.conInteres,
    sobrepagoCapital: Math.max(0, capitalPagado - detalles.monto),
    estaSaldado: deuda.conInteres <= CENTAVO
  };
};

/**
 * Resumen del estado REAL de un préstamo hoy, sacado del mismo cronograma
 * que se ve en la tabla (antes este panel usaba números del contrato
 * original y no cuadraba: contaba meses de "solo interés" como cuotas
 * pagadas y mezclaba interés ya vencido con interés de meses futuros).
 *
 *  - cuotasPagadas: meses en que se pagó capital.
 *  - mesesSoloInteres: meses en que solo se pagó interés.
 *  - mesesTranscurridos: períodos que ya corrieron hasta hoy.
 *  - interesAcumulado: interés generado hasta hoy (meses × interés mensual).
 *  - interesPendiente: interés vencido que debe hoy.
 *  - capitalPendiente / deudaHoy: lo que debe hoy (= "Adeudado con interés").
 *  - mesesRestantes: capital pendiente ÷ capital mensual, redondeado arriba.
 *  - interesFuturo / totalParaTerminar: si paga puntual desde ahora.
 */
export const calcularPagosRealizados = (prestamo, detalles, registrosDelSocio = []) => {
  const det = detalles || calcularCuotaMensual(prestamo);
  const finalizado = prestamo.estado === 'finalizada';
  const { registros } = obtenerRegistrosDelPrestamo(prestamo, registrosDelSocio);
  const conPago = registros.filter(tienePagoDePrestamo);
  const capitalPagado = conPago.reduce((s, r) => s + (r.capitalPrestamo || 0), 0);
  const interesPagado = conPago.reduce((s, r) => s + (r.interesPrestamo || 0), 0);

  const cronograma = generarCronogramaAmortizacion(prestamo, det, registrosDelSocio);
  const cuotasPagadas = cronograma.filter(c => c.estado === 'Pagado').length;
  const mesesSoloInteres = cronograma.filter(c => c.estado === 'Pagó Interés').length;

  const hoy = obtenerPeriodoDeHoy(cronograma);
  const mesesTranscurridos = hoy ? hoy.periodo : 0;
  const capitalPendiente = finalizado || !hoy ? (finalizado ? 0 : det.monto) : parseFloat(hoy.saldoRestante);
  const interesPendiente = finalizado || !hoy ? 0 : Math.max(0, parseFloat(hoy.saldoTotalConInteres) - parseFloat(hoy.saldoRestante));

  const capitalMensual = det.plazo > 0 ? det.monto / det.plazo : 0;
  const mesesRestantes = capitalPendiente > CENTAVO && capitalMensual > 0
    ? Math.ceil((capitalPendiente - CENTAVO) / capitalMensual)
    : 0;
  const interesFuturo = mesesRestantes * det.interesAlMes;

  return {
    cuotasPagadas,
    mesesSoloInteres,
    mesesTranscurridos,
    mesesRestantes,
    capitalPagado,
    interesPagado,
    totalPagado: capitalPagado + interesPagado,
    interesAcumulado: Math.round(det.interesAlMes * mesesTranscurridos * 100) / 100,
    interesPendiente: Math.round(interesPendiente * 100) / 100,
    capitalPendiente: Math.round(capitalPendiente * 100) / 100,
    deudaHoy: Math.round((capitalPendiente + interesPendiente) * 100) / 100,
    capitalMensual: Math.round(capitalMensual * 100) / 100,
    interesFuturo: Math.round(interesFuturo * 100) / 100,
    totalParaTerminar: Math.round((capitalPendiente + interesPendiente + interesFuturo) * 100) / 100
  };
};
